/*
 * core.js — the arithmetic of /true-size-map. Pure functions, no DOM.
 *
 *   TrueSize.decode(json)            → [{ key, a3, a2, type, area, pop, names, search, parts }]
 *   TrueSize.prepare(shape)          → shape.geo: densified rings + (δ, β) of every vertex from its centre
 *   TrueSize.place(shape, lat, lon, rot) → { rings:[[lat,lon…]], maxLat } — the outline moved there
 *   TrueSize.moveTo(shape, target, from) → the furthest centre toward `target` that keeps off the poles
 *   TrueSize.mercatorScale(lat)      → how much Web Mercator inflates AREA at that latitude
 *   TrueSize.lambert(shape, …)       → the outline in an equal-area projection, for the honest comparison
 *
 * How a shape moves. Every vertex is stored as a distance δ and a bearing β
 * from the shape's centre; placed at a new centre c with a rotation ρ, vertex
 * = destination(c, δ, β + ρ). That map is a rigid rotation of the sphere (the
 * unique one taking the old centre to c and the old «north» to the new north,
 * turned by ρ), so area, angles and distances are preserved EXACTLY — what
 * changes on screen is only Mercator's inflation, which is the whole point.
 * The naive alternative — scaling the outline by the cosine of the latitude —
 * keeps the country's own distortion inside it: Chile would stay squashed at
 * one end and stretched at the other.
 *
 * Edges are densified to ≤ 0.5° before anything else: a GeoJSON edge is
 * straight in lon/lat, so the 49th-parallel border would stay a straight line
 * after a move instead of bending into the arc it becomes.
 */
(function () {
    'use strict';
    var RAD = Math.PI / 180, DEG = 180 / Math.PI;
    var MAX_LAT = 84;           // Web Mercator ends at 85.05°; the pole is where competitors' shapes «explode»

    /* The rotation runs on the AUTHALIC sphere — the sphere with the same area
     * as the WGS84 ellipsoid, onto which latitudes map equal-area. A rotation
     * of geodetic coordinates would be a rigid motion of a sphere the Earth is
     * not: measured on real outlines, Greenland moved to the equator gained or
     * lost 1.2 % of its area. Through authalic latitude the area is preserved
     * exactly on the ellipsoid itself. */
    var A_ = 6378137.0, F_ = 1 / 298.257223563, E2_ = F_ * (2 - F_), E_ = Math.sqrt(E2_);
    function q_(phi) { var s = Math.sin(phi); return (1 - E2_) * (s / (1 - E2_ * s * s) - (1 / (2 * E_)) * Math.log((1 - E_ * s) / (1 + E_ * s))); }
    var QP_ = q_(Math.PI / 2);
    function toAuth(latDeg) { return Math.asin(Math.max(-1, Math.min(1, q_(latDeg * RAD) / QP_))) * DEG; }
    var e2 = E2_, e4 = e2 * e2, e6 = e4 * e2;
    var C1 = e2 / 3 + 31 * e4 / 180 + 517 * e6 / 5040, C2 = 23 * e4 / 360 + 251 * e6 / 3780, C3 = 761 * e6 / 45360;
    function fromAuth(betaDeg) { var b = betaDeg * RAD; return (b + C1 * Math.sin(2 * b) + C2 * Math.sin(4 * b) + C3 * Math.sin(6 * b)) * DEG; }

    function decode(json) {
        var q = json.q || 1e4, out = [];
        (json.f || []).forEach(function (f) {
            var parts = [], cur = null;
            (f.g || []).forEach(function (r) {
                var hole = !Array.isArray(r);
                var flat = hole ? r.r : r;
                var n = flat.length / 2, lat = new Float64Array(n), lon = new Float64Array(n), x = 0, y = 0;
                for (var i = 0; i < n; i++) { x += flat[i * 2]; y += flat[i * 2 + 1]; lon[i] = x / q; lat[i] = y / q; }
                if (!hole || !cur) { cur = { outer: { lat: lat, lon: lon }, holes: [] }; parts.push(cur); }
                else cur.holes.push({ lat: lat, lon: lon });
            });
            out.push({ key: f.k, a3: f.a3, a2: f.a2, type: f.t, area: f.a, pop: f.p, popYear: f.py, names: f.n || {}, search: f.s || '', parts: parts });
        });
        return out;
    }

    function toVec(lat, lon) {
        var a = lat * RAD, b = lon * RAD, c = Math.cos(a);
        return [c * Math.cos(b), c * Math.sin(b), Math.sin(a)];
    }
    function fromVec(v) {
        var r = Math.sqrt(v[0] * v[0] + v[1] * v[1] + v[2] * v[2]) || 1;
        return [Math.asin(Math.max(-1, Math.min(1, v[2] / r))) * DEG, Math.atan2(v[1], v[0]) * DEG];
    }

    function densify(lat, lon) {
        var la = [], lo = [], n = lat.length;
        for (var i = 0; i < n; i++) {
            var j = (i + 1) % n;
            la.push(lat[i]); lo.push(lon[i]);
            var dLon = lon[j] - lon[i];
            if (dLon > 180) dLon -= 360; else if (dLon < -180) dLon += 360;
            var dLat = lat[j] - lat[i];
            var steps = Math.ceil(Math.max(Math.abs(dLon), Math.abs(dLat)) / 0.5);
            for (var s = 1; s < steps; s++) { la.push(lat[i] + dLat * s / steps); lo.push(lon[i] + dLon * s / steps); }
        }
        return { lat: la, lon: lo };
    }

    /* The centre a shape is carried by: the mean of its outline points on the
     * unit sphere, weighted by edge length, over the part holding most of the
     * area. For a country of islands (Indonesia, Japan) the parts are summed. */
    function centre(shape) {
        var sx = 0, sy = 0, sz = 0;
        shape.parts.forEach(function (P) {
            var r = P.outer, n = r.lat.length;
            for (var i = 0; i < n; i++) {
                var j = (i + 1) % n, v = toVec(r.lat[i], r.lon[i]), w = toVec(r.lat[j], r.lon[j]);
                var len = Math.acos(Math.max(-1, Math.min(1, v[0] * w[0] + v[1] * w[1] + v[2] * w[2])));
                sx += (v[0] + w[0]) * len; sy += (v[1] + w[1]) * len; sz += (v[2] + w[2]) * len;
            }
        });
        return fromVec([sx, sy, sz]);
    }

    function polar(c, lat, lon) { // → [δ (rad), β (rad)] from centre c
        var p1 = c[0] * RAD, p2 = lat * RAD, dl = (lon - c[1]) * RAD;
        var s1 = Math.sin((p2 - p1) / 2), s2 = Math.sin(dl / 2);
        var h = s1 * s1 + Math.cos(p1) * Math.cos(p2) * s2 * s2;
        var d = 2 * Math.asin(Math.min(1, Math.sqrt(h)));
        var b = Math.atan2(Math.sin(dl) * Math.cos(p2), Math.cos(p1) * Math.sin(p2) - Math.sin(p1) * Math.cos(p2) * Math.cos(dl));
        return [d, b];
    }

    function prepare(shape) {
        if (shape.geo) return shape.geo;
        var cg = centre(shape), c = [toAuth(cg[0]), cg[1]];
        var rings = [];
        shape.parts.forEach(function (P) {
            [P.outer].concat(P.holes).forEach(function (r, k) {
                var d = densify(r.lat, r.lon), n = d.lat.length;
                var sd = new Float64Array(n), cd = new Float64Array(n), be = new Float64Array(n);
                for (var i = 0; i < n; i++) {
                    var pb = polar(c, toAuth(d.lat[i]), d.lon[i]);
                    sd[i] = Math.sin(pb[0]); cd[i] = Math.cos(pb[0]); be[i] = pb[1];
                }
                rings.push({ hole: k > 0, n: n, sd: sd, cd: cd, be: be });
            });
        });
        shape.geo = { c: cg, ca: c, rings: rings };
        return shape.geo;
    }

    /* The outline placed with its centre at (lat, lon), turned by rot degrees.
     * Longitudes are unwrapped around the new centre, so a shape straddling
     * the antimeridian (Chukotka, the Aleutians, Fiji) stays one piece. */
    function place(shape, lat, lon, rot) {
        var g = prepare(shape);
        var pc = toAuth(lat) * RAD, sp = Math.sin(pc), cp = Math.cos(pc), r0 = (rot || 0) * RAD;
        var out = [], maxLat = -90, minLat = 90;
        g.rings.forEach(function (R) {
            var la = new Float64Array(R.n), lo = new Float64Array(R.n);
            for (var i = 0; i < R.n; i++) {
                var b = R.be[i] + r0, sd = R.sd[i], cd = R.cd[i];
                var sinLat = sp * cd + cp * sd * Math.cos(b);
                var phi = Math.asin(Math.max(-1, Math.min(1, sinLat)));
                var dl = Math.atan2(Math.sin(b) * sd * cp, cd - sp * sinLat);
                var L = lon + dl * DEG;
                while (L - lon > 180) L -= 360;
                while (L - lon < -180) L += 360;
                la[i] = fromAuth(phi * DEG); lo[i] = L;
                if (la[i] > maxLat) maxLat = la[i];
                if (la[i] < minLat) minLat = la[i];
            }
            out.push({ hole: R.hole, lat: la, lon: lo });
        });
        return { rings: out, maxLat: maxLat, minLat: minLat };
    }

    /* A shape fits when no vertex enters the polar band AND no ring goes round
     * a pole. The vertex test alone passes Africa centred at 75° N: its
     * vertices stay below 79° while its outline encloses the pole, and Mercator
     * draws that as a band round the whole world — the very explosion the
     * band exists to prevent (measured 2026-09-24; a fast drag could jump
     * straight there, since moveTo accepts any target that «fits»). */
    function encirclesPole(lon) {
        var s = 0, n = lon.length;
        for (var i = 0; i < n; i++) {
            var d = lon[(i + 1) % n] - lon[i];
            if (d > 180) d -= 360; else if (d < -180) d += 360;
            s += d;
        }
        return Math.abs(s) > 180;
    }
    function fits(shape, lat, lon, rot) {
        var p = place(shape, lat, lon, rot);
        if (p.maxLat > MAX_LAT || p.minLat < -MAX_LAT) return false;
        for (var i = 0; i < p.rings.length; i++) if (!p.rings[i].hole && encirclesPole(p.rings[i].lon)) return false;
        return true;
    }

    /* Toward `target`, as far as the shape can go without touching the band
     * Mercator cannot draw. Bisection along the straight line in lat/lon —
     * the drag feels like a wall, not like a jump back. */
    function moveTo(shape, target, from, rot) {
        if (fits(shape, target[0], target[1], rot)) return target;
        if (!from || !fits(shape, from[0], from[1], rot)) return from;
        var lo = 0, hi = 1;
        for (var it = 0; it < 14; it++) {
            var m = (lo + hi) / 2;
            if (fits(shape, from[0] + (target[0] - from[0]) * m, from[1] + (target[1] - from[1]) * m, rot)) lo = m; else hi = m;
        }
        return [from[0] + (target[0] - from[0]) * lo, from[1] + (target[1] - from[1]) * lo];
    }

    /* A turn obeys the same wall as a move. Turned near the top, a wide shape
     * grows north into the band (Canada at its highest place turned 90°
     * reached 88.9° — measured 2026-09-25): it slides toward the equator just
     * enough instead. null: at this angle the shape fits nowhere. */
    function turn(shape, lat, lon, rot) {
        if (fits(shape, lat, lon, rot)) return [lat, lon];
        if (!fits(shape, 0, lon, rot)) return null;
        return moveTo(shape, [lat, lon], [0, lon], rot);
    }

    /* A drawn outline travels inside the share link: «lat_lon~lat_lon~…», four
     * decimals (≈ 11 m). Anything unreadable is dropped; fewer than three
     * points is no outline. */
    function encodeOutline(lat, lon) {
        var out = [];
        for (var i = 0; i < lat.length; i++) out.push(lat[i].toFixed(4) + '_' + lon[i].toFixed(4));
        return out.join('~');
    }
    function decodeOutline(s) {
        var lat = [], lon = [];
        String(s || '').split('~').forEach(function (p) {
            var v = p.split('_'), a = parseFloat(v[0]), b = parseFloat(v[1]);
            if (v.length === 2 && isFinite(a) && isFinite(b) && Math.abs(a) <= 90 && Math.abs(b) <= 540) { lat.push(a); lon.push(b); }
        });
        return lat.length >= 3 ? { lat: lat, lon: lon } : null;
    }

    function destination(lat, lon, d, b) { // d, b in radians
        var p1 = lat * RAD, sp = Math.sin(p1), cp = Math.cos(p1);
        var sl = sp * Math.cos(d) + cp * Math.sin(d) * Math.cos(b);
        var p2 = Math.asin(Math.max(-1, Math.min(1, sl)));
        var dl = Math.atan2(Math.sin(b) * Math.sin(d) * cp, Math.cos(d) - sp * sl);
        return [p2 * DEG, lon + dl * DEG];
    }

    /* Area inflation of Web Mercator at a latitude: sec²φ. At 60° a shape is
     * drawn 4× its true size, at 72° 10.5×, at 80° 33×. */
    function mercatorScale(lat) { var c = Math.cos(lat * RAD); return c > 0.01 ? 1 / (c * c) : 1e4; }

    /* Lambert azimuthal equal-area around the shape's own centre: every outline
     * drawn at the same scale is compared by its TRUE area. Returns rings in
     * kilometres (x east, y north). */
    function lambert(shape, rot) {
        var p = place(shape, 0, 0, rot || 0), R = 6371.0072, out = [];
        p.rings.forEach(function (r) {
            var n = r.lat.length, xs = new Float64Array(n), ys = new Float64Array(n);
            for (var i = 0; i < n; i++) {
                var phi = r.lat[i] * RAD, lam = r.lon[i] * RAD;
                var k = Math.sqrt(2 / (1 + Math.cos(phi) * Math.cos(lam)));
                xs[i] = R * k * Math.cos(phi) * Math.sin(lam);
                ys[i] = R * k * Math.sin(phi);
            }
            out.push({ hole: r.hole, x: xs, y: ys });
        });
        return out;
    }

    /* Area of a drawn polygon (the «your own shape» tool), km², WGS84 authalic. */
    var A = 6378137.0, F = 1 / 298.257223563, E2 = F * (2 - F), E = Math.sqrt(E2);
    function qOf(phi) { var s = Math.sin(phi); return (1 - E2) * (s / (1 - E2 * s * s) - (1 / (2 * E)) * Math.log((1 - E * s) / (1 + E * s))); }
    var QP = qOf(Math.PI / 2), RA = A * Math.sqrt(QP / 2);
    function ringArea(lat, lon) {
        var t = 0, n = lat.length;
        for (var i = 0; i < n; i++) {
            var j = (i + 1) % n, dl = lon[j] - lon[i];
            if (dl > 180) dl -= 360; else if (dl < -180) dl += 360;
            t += dl * RAD * (2 + qOf(lat[i] * RAD) / QP + qOf(lat[j] * RAD) / QP);
        }
        return Math.abs(t * RA * RA / 2) / 1e6;
    }

    window.TrueSize = {
        decode: decode, prepare: prepare, place: place, moveTo: moveTo, fits: fits, destination: destination,
        polar: polar, centre: centre, mercatorScale: mercatorScale, lambert: lambert, ringArea: ringArea,
        toVec: toVec, fromVec: fromVec, toAuth: toAuth, fromAuth: fromAuth, MAX_LAT: MAX_LAT,
        turn: turn, encodeOutline: encodeOutline, decodeOutline: decodeOutline
    };
})();
